import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Globe, Palette, Sparkles, ArrowRight } from "lucide-react";

export const GhostWidgetPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [chosenColor, setChosenColor] = useState("#8b5cf6");
  const navigate = useNavigate();

  useEffect(() => {
    // Show popup after 0.5 seconds - always show on landing page load
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!websiteUrl.trim()) return;

    // Clean up the URL
    let cleanUrl = websiteUrl.trim();
    if (!cleanUrl.startsWith("http://") && !cleanUrl.startsWith("https://")) {
      cleanUrl = "https://" + cleanUrl;
    }

    // Encode the URL and color for the route
    const encodedUrl = encodeURIComponent(cleanUrl);
    const encodedColor = encodeURIComponent(chosenColor);
    
    navigate(`/preview?url=${encodedUrl}&color=${encodedColor}`);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-purple-500/30 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            See It On Your Site
          </DialogTitle>
          <DialogDescription className="text-center text-white/70">
            Preview how our chat widget looks on your website — no code required!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="website" className="text-white/90 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Your Website URL
            </Label>
            <Input
              id="website"
              type="text"
              placeholder="example.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-purple-500"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-white/90 flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Widget Color
            </Label>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={chosenColor}
                onChange={(e) => setChosenColor(e.target.value)}
                className="w-14 h-14 rounded-lg cursor-pointer border-2 border-white/20 bg-transparent"
                style={{ padding: 0 }}
              />
              <Input
                type="text"
                value={chosenColor}
                onChange={(e) => setChosenColor(e.target.value)}
                placeholder="#8b5cf6"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-purple-500 font-mono uppercase"
                maxLength={7}
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 border-0 shadow-lg hover:shadow-xl transition-all"
            disabled={!websiteUrl.trim()}
          >
            Preview Widget
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </form>

        <p className="text-xs text-center text-white/50 mt-2">
          No installation needed — just a preview!
        </p>
      </DialogContent>
    </Dialog>
  );
};
